import { useLayoutEffect, useRef, useState } from "react";
import { observeTextLayout } from "../utils/observeTextLayout";

/**
 * Whether a text shown on one line is wider than its element, so the end would be cut off.
 *
 * <p>Measured after layout, after a resize and after web fonts load, while `enabled` is true. A
 * character count cannot answer this: a 77-character sentence can already be wider than its
 * column, and the width depends on the screen and the font. Once a text overflows, the answer
 * stays for that text, so the caller does not switch views back and forth at a boundary width. A
 * new text is measured again.
 */
export function useOverflowsOneLine<T extends HTMLElement>(text: string, enabled: boolean) {
  const ref = useRef<T>(null);
  const [overflowingText, setOverflowingText] = useState<string | null>(null);
  const overflows = overflowingText === text;
  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || overflows || !element) return;
    const measure = () => { if (element.scrollWidth > element.clientWidth) setOverflowingText(text); };
    measure();
    return observeTextLayout(element, measure);
  }, [enabled, overflows, text]);
  return { ref, overflows };
}
